import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import RotaResources from "../resources/RotaResources";
import LocationResources from "../resources/LocationResources";
import EventResources from "../resources/EventResources";
import RotaRoleResources from "../resources/RotaRoleResources";
import ConfigResources from "../resources/ConfigResources";
import { logout } from "../redux/actions/LoggedInAction";
import { connect } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import Select from 'react-select';
import moment from "moment";
import {ACCESS_DENIED} from '../Messages'
import toast from 'react-hot-toast'

function CreateRota({isLoggedIn, loggedInUser, logout}){
    const navigate = useNavigate();

    const [creationType, setCreationType] = useState('monthly')
    const [locations, setLocations] = useState([])
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [events, setEvents] = useState([])
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [rotaRoles, setRotaRoles] = useState([])
    const [selectedRoles, setSelectedRoles] = useState([])
    const [closeUpMapping, setCloseUpMapping] = useState([])
    const [roamingMapping, setRoamingMapping] = useState([])
    const [isGenerating, setIsGenerating] = useState(false)
    const [allowRotaGenOverride, setAllowRotaGenOverride] = useState(false)
    const [targetMonth, setTargetMonth] = useState(moment().add(1, 'month')) // per-location: current or next month
    const [checkingMonthRota, setCheckingMonthRota] = useState(false)

    let rota_resources = new RotaResources()
    const locationResources = new LocationResources()
    const eventResources = new EventResources()
    const rotaRoleResources = new RotaRoleResources()
    const configResources = new ConfigResources()

    const loadLocations = () => {
        return locationResources.get()
    }

    const loadEvents = () => {
        return eventResources.get_admin()
    }

    const loadRotaRoles = () => {
        return rotaRoleResources.getRoles()
    }

    const handleCreationTypeChange = (e) => {
        setCreationType(e.target.value)
        setSelectedLocation(null)
        setSelectedEvent(null)
        setSelectedRoles([])
        setCloseUpMapping([])
        setRoamingMapping([])
    }

    const handleLocationSelection = (selectedOption) => {
        setSelectedLocation(selectedOption)
        // Determine if current month already has a rota for this location
        setCheckingMonthRota(true)
        const currentMonthDate = moment().format('DD-MM-YYYY HH:mm:ss')
        rota_resources.get_monthly_rota(currentMonthDate, selectedOption.value).then(response => {
            setCheckingMonthRota(false)
            const hasRota = response.body && !response.body.error
            if (hasRota) {
                // Rota already exists for current month — offer next month
                setTargetMonth(moment().add(1, 'month'))
            } else {
                // No rota for current month — offer current month
                setTargetMonth(moment())
            }
        }).catch(() => {
            setCheckingMonthRota(false)
            // Default to current month on error (assume no rota exists)
            setTargetMonth(moment())
        })
    }

    const handleEventSelection = (selectedOption) => {
        setSelectedEvent(selectedOption)
    }

    const handleRoleSelection = (selectedOptions) => {
        setSelectedRoles(selectedOptions || [])
    }

    const canCreateNextMonthRota = () => {
        const today = moment()
        const nextMonth = moment().add(1, 'month').startOf('month')
        const daysUntilNextMonth = nextMonth.diff(today, 'days')
        return daysUntilNextMonth <= 12
    }

    const handleCreateRota = () => {
        if (creationType === 'monthly') {
            handleCreateMonthlyRota()
        } else {
            handleCreateEventRota()
        }
    }

    const handleCreateMonthlyRota = () => {
        if (!selectedLocation) {
            toast.error("Please select a location")
            return
        }

        const isCurrentMonth = targetMonth.isSame(moment(), 'month')
        const daysUntilNextMonth = moment().add(1, 'month').startOf('month').diff(moment(), 'days')

        // Only apply the 10-day restriction when targeting next month
        if (!isCurrentMonth && daysUntilNextMonth > 10 && !allowRotaGenOverride) {
            toast.error("Monthly rota can only be created 10 days before the next month")
            return
        }

        let confirmMessage = isCurrentMonth
            ? `You are about to create the rota for ${targetMonth.format('MMMM YYYY')} (current month).\n\n`
            : `You are ${daysUntilNextMonth} days away from ${targetMonth.format('MMMM YYYY')}.\n\n`

        if (selectedLocation.label.toLowerCase().includes('canary wharf')) {
            confirmMessage += "Please ensure the team configuration is checked before proceeding.\n\n"
        }

        confirmMessage += "Do you want to proceed to create the rota?"

        const confirmed = window.confirm(confirmMessage)
        if (!confirmed) return

        const rotaData = {
            event_date: targetMonth.format('YYYY-MM-DD HH:mm:ss'),
            location_id: selectedLocation.value,
            monthly_generation: true
        }

        setIsGenerating(true)
        rota_resources.create_monthly_rota(rotaData).then(response => {
            setIsGenerating(false)
            if (response.error && response.error.length > 0) {
                toast.error("Failed to create monthly rota: " + response.error)
            } else {
                toast.success("Monthly rota created successfully")
                setTimeout(() => {
                    window.location.href = window.location.origin + '/managerota';
                  }, 200);
            }
        }).catch((err) => {
            setIsGenerating(false)
            toast.error("Failed to create monthly rota: " + err)
        })
    }

    const handleCreateEventRota = () => {
        if (!selectedEvent) {
            toast.error("Please select an event")
            return
        }

        // If roles are selected, validate that at least one role mapping is complete
        if (selectedRoles && selectedRoles.length > 0) {
            if (closeUpMapping.length === 0 && roamingMapping.length === 0) {
                toast.error("Please map at least one core role (Close Up Camera or Roaming Camera)")
                return
            }
        }

        // Build role_mappings in the format: {custom_role_id: "Close_Up_Camera" or "Roaming_Camera"}
        let roleMappings = null
        if (selectedRoles && selectedRoles.length > 0 && (closeUpMapping.length > 0 || roamingMapping.length > 0)) {
            roleMappings = {}
            closeUpMapping.forEach(r => { roleMappings[r.value] = "Close_Up_Camera" })
            roamingMapping.forEach(r => { roleMappings[r.value] = "Roaming_Camera" })
        }

        const rotaData = {
            event_id: selectedEvent.value,
            requested_roles: selectedRoles && selectedRoles.length > 0 ? selectedRoles.map(role => role.value) : [],
            role_mappings: roleMappings,
            monthly_generation: false
        }

        setIsGenerating(true)
        rota_resources.create_event_rota(rotaData).then(response => {
            setIsGenerating(false)
            if (response.error && response.error.length > 0) {
                toast.error("Failed to create event rota: " + response.error)
            } else {
                toast.success("Event rota created successfully")
                navigate('/managerota')
            }
        }).catch((err) => {
            setIsGenerating(false)
            toast.error("Failed to create event rota: " + err)
        })
    }

    useEffect(() => {
        loadLocations().then(response => {
            setLocations(response['body'])
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        loadEvents().then(response => {
            setEvents(response['body'])
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        loadRotaRoles().then(response => {
            const roles = response['body']
            setRotaRoles(roles)
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        configResources.get_by_name('ALLOW_ROTA_GEN_OVERRIDE').then(response => {
            const config = response['body']
            if (config && (config['value'] === '1' || config['value'] === 1)) {
                setAllowRotaGenOverride(true)
            }
        }).catch(() => {})
    }, [])

    // Filter locations that use a rota
    const rotaLocations = locations
        .filter(loc => loc['uses_rota'] === "True" || loc['uses_rota'] === true || loc['uses_rota'] === 1)
        .map(loc => ({
            value: loc['id'],
            label: loc['name']
        }))

    const currentMonth = moment()
    const nextMonth = moment().add(1, 'month')
    const crossesYear = currentMonth.year() !== nextMonth.year()
    const upcomingApprovedEvents = events
        .filter(event => {
            const eventDate = moment(event['event_date'])
            const isEnabled = event['enabled'] === '1' || event['enabled'] === 1 || event['enabled'] === true
            const isCurrentMonth = eventDate.month() === currentMonth.month() && eventDate.year() === currentMonth.year()
            const isNextMonth = eventDate.month() === nextMonth.month() && eventDate.year() === nextMonth.year()
            return (isCurrentMonth || isNextMonth) &&
                eventDate.isSameOrAfter(moment(), 'day') &&
                isEnabled
        })
        .map(event => ({
            value: event['id'],
            label: event['name']
        }))

    // Custom roles (non-core roles)
    const customRoles = rotaRoles
        .filter(role => !role.is_core_role)
        .map(role => ({
            value: role.id,
            label: role.name
        }))

    // All rota roles for multi-select
    const allRotaRoles = rotaRoles.map(role => ({
        value: role.id,
        label: role.name
    }))
    

    return(
        <Fragment>
            <Topbar/>

            <div className="hero-area">
                <div className="page-banner parallax" style={{backgroundColor:"#003165"}}>
                    <div className="container">
                        <div className="page-banner-text">
                            {isLoggedIn ?
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>Create Rota</strong></h1>
                                :
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {isGenerating && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,49,101,0.85)',
                    zIndex: 9999,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#fff'
                }}>
                    <div style={{
                        width: '60px', height: '60px',
                        border: '6px solid rgba(255,168,0,0.3)',
                        borderTop: '6px solid #ffa800',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '20px'
                    }} />
                    <h3 style={{margin: 0, color: '#ffa800'}}>Generating Rota...</h3>
                    <p style={{marginTop: '8px', color: '#ccc', fontSize: '14px'}}>This may take a few seconds, please wait.</p>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}

            <div className="content">
                {isLoggedIn ? (
                    <div className="container">
                        <div className="spacer-40"></div>

                        <div style={{backgroundColor: '#fff8e1', border: '1px solid #ffa800', borderRadius: '8px', padding: '14px 18px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                            <i className="fa fa-exclamation-triangle" style={{color: '#ffa800', fontSize: '18px'}}></i>
                            <span style={{color: '#003165', fontSize: 'clamp(13px, 3.5vw, 14px)'}}>
                                Before generating a rota, please ensure the configurable values are up to date. <a href="/manageconfig" style={{color: '#ffa800', fontWeight: 'bold', textDecoration: 'underline'}}>Check rota config values here</a>
                            </span>
                        </div>

                        {/* Radio buttons for creation type */}
                        <div className="row">
                            <div className="col-md-12">
                                <h3 style={{color:"#ffa800"}}><strong>Select Rota Type</strong></h3>
                                <div className="spacer-20"></div>
                                <div>
                                    <label style={{marginRight: "20px"}}>
                                        <input
                                            type="radio"
                                            value="monthly"
                                            checked={creationType === 'monthly'}
                                            onChange={handleCreationTypeChange}
                                            style={{marginRight: "5px"}}
                                        />
                                        Monthly Rota
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            value="event"
                                            checked={creationType === 'event'}
                                            onChange={handleCreationTypeChange}
                                            style={{marginRight: "5px"}}
                                        />
                                        Event Rota
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="spacer-40"></div>

                        {/* Monthly Rota Creation */}
                        {creationType === 'monthly' && (
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                padding: '30px',
                                borderRadius: '8px',
                                border: '2px solid #ffa800'
                            }}>
                                <h3 style={{color:"#003165", marginBottom: '20px'}}>
                                    <strong>
                                        {checkingMonthRota
                                            ? 'Checking existing rota...'
                                            : selectedLocation
                                                ? `${targetMonth.format('MMMM YYYY')} Rota Generation`
                                                : 'Select a location to determine target month'
                                        }
                                    </strong>
                                </h3>

                                <div className="row">
                                    <div className="col-md-6">
                                        <label style={{color:"#ffa800"}}>Select Location</label>
                                        <Select
                                            name='location'
                                            id="location"
                                            classNamePrefix='select-css-tag'
                                            value={selectedLocation}
                                            onChange={handleLocationSelection}
                                            options={rotaLocations}
                                            placeholder="Select Location"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Event Rota Creation */}
                        {creationType === 'event' && (
                            <div style={{
                                backgroundColor: '#f5f5f5',
                                padding: '30px',
                                borderRadius: '8px',
                                border: '2px solid #ffa800'
                            }}>
                                <h3 style={{color:"#003165", marginBottom: '20px'}}>
                                    <strong>Event Rota Creation</strong>
                                </h3>

                                <div className="row">
                                    <div className="col-md-6">
                                        <label style={{color:"#ffa800"}}><strong>{currentMonth.format(crossesYear ? 'MMMM YYYY' : 'MMMM')} & {nextMonth.format('MMMM YYYY')} Approved Events</strong></label>
                                        <Select
                                            name='event'
                                            id="event"
                                            classNamePrefix='select-css-tag'
                                            value={selectedEvent}
                                            onChange={handleEventSelection}
                                            options={upcomingApprovedEvents}
                                            placeholder="Select Event"
                                        />
                                    </div>
                                </div>

                                {selectedEvent && (
                                    <>
                                        <div className="spacer-30"></div>

                                                                <div className="row">
                                                                    <div className="col-md-12">
                                                                        <label style={{color:"#ffa800"}}>Requested Roles (Optional)</label>
                                                                        <div style={{
                                                                            backgroundColor: '#fff3cd',
                                                                            border: '1px solid #ffc107',
                                                                            padding: '10px',
                                                                            borderRadius: '4px',
                                                                            marginBottom: '10px',
                                                                            color: '#856404'
                                                                        }}>
                                                                            <strong>Note:</strong> If you request roles, only those roles will be used in the rota creation.
                                                                            You can add more custom roles in the Manage Rota Roles section.
                                                                        </div>
                                                                        <Select
                                                                            name='requested_roles'
                                                                            id="requested_roles"
                                                                            classNamePrefix='select-css-tag'
                                                                            value={selectedRoles}
                                                                            onChange={handleRoleSelection}
                                                                            options={allRotaRoles}
                                                                            placeholder="Select Roles (Optional)"
                                                                            isMulti
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {selectedRoles && selectedRoles.length > 0 && (
                                                                    <>
                                                                        <div className="spacer-30"></div>

                                                                        <div className="row">
                                                                            <div className="col-md-12">
                                                                                <label style={{color:"#ffa800", marginBottom: '15px'}}>
                                                                                    <strong>Role Mapping * (At least one required)</strong>
                                                                                </label>
                                                                                <TableContainer component={Paper}>
                                                                                    <Table>
                                                                                        <TableHead>
                                                                                            <TableRow>
                                                                                                <TableCell><strong>Core Role</strong></TableCell>
                                                                                                <TableCell><strong>Custom Role</strong></TableCell>
                                                                                            </TableRow>
                                                                                        </TableHead>
                                                                                        <TableBody>
                                                                                            <TableRow>
                                                                                                <TableCell>Close Up Camera</TableCell>
                                                                                                <TableCell>
                                                                                                    <Select
                                                                                                        name='close_up_mapping'
                                                                                                        classNamePrefix='select-css-tag'
                                                                                                        value={closeUpMapping}
                                                                                                        onChange={(opts) => setCloseUpMapping(opts || [])}
                                                                                                        options={customRoles}
                                                                                                        placeholder="Select custom roles"
                                                                                                        isMulti
                                                                                                    />
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                            <TableRow>
                                                                                                <TableCell>Roaming Camera</TableCell>
                                                                                                <TableCell>
                                                                                                    <Select
                                                                                                        name='roaming_mapping'
                                                                                                        classNamePrefix='select-css-tag'
                                                                                                        value={roamingMapping}
                                                                                                        onChange={(opts) => setRoamingMapping(opts || [])}
                                                                                                        options={customRoles}
                                                                                                        placeholder="Select custom roles"
                                                                                                        isMulti
                                                                                                    />
                                                                                                </TableCell>
                                                                                            </TableRow>
                                                                                        </TableBody>
                                                                                    </Table>
                                                                                </TableContainer>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="spacer-40"></div>
                        <div className="row">
                            <div className="col-md-12">
                                <button
                                    onClick={handleCreateRota}
                                    disabled={
                                        isGenerating ||
                                        (creationType === 'monthly' && !selectedLocation) ||
                                        (creationType === 'event' && (
                                            !selectedEvent ||
                                            (selectedRoles.length > 0 && closeUpMapping.length === 0 && roamingMapping.length === 0)
                                        ))
                                    }
                                    style={{
                                        backgroundColor:
                                            (creationType === 'monthly' && selectedLocation) ||
                                            (creationType === 'event' && selectedEvent && (
                                                selectedRoles.length === 0 ||
                                                (selectedRoles.length > 0 && (closeUpMapping.length > 0 || roamingMapping.length > 0))
                                            ))
                                            ? '#ffa800' : '#ccc',
                                        color: '#fff',
                                        padding: '12px 30px',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor:
                                            (creationType === 'monthly' && selectedLocation) ||
                                            (creationType === 'event' && selectedEvent && (
                                                selectedRoles.length === 0 ||
                                                (selectedRoles.length > 0 && (closeUpMapping.length > 0 || roamingMapping.length > 0))
                                            ))
                                            ? 'pointer' : 'not-allowed',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Create Rota
                                </button>
                            </div>
                        </div>

                        <div className="spacer-40"></div>
                    </div>
                ) : <></>}
            </div>
        </Fragment>
    )
}

const mapStateToProps = (state) => {
    return{
       isLoggedIn: state.authReducer.isLoggedIn,
       loggedInUser: state.authReducer.user
    };
    
};

const mapDispatchToProps = {
    logout
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateRota);